import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_SETTLEMENTS, GET_SETTLEMENT_OVERVIEW } from '../graphql/queries';
import { SETTLE_NOW, VERIFY_SETTLEMENT } from '../graphql/mutations';
import Layout from '../components/Layout';
import { useAuth } from '../utils/authContext';
import { formatDateTime } from '../utils/dateFormat';
import LoadingSpinner from '../components/LoadingSpinner';
import { ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const SettlementStatus = {
    AUTOMATIC: 'bg-green-100 text-green-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
    PROCESSING: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-green-100 text-green-800',
    FAILED: 'bg-red-100 text-red-800'
};



const Settlements = () => {
    const { user } = useAuth();
    const [filters, setFilters] = useState({
        status: '',
        startDate: '',
        endDate: ''
    });
    const [page, setPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    const { loading, error, data, refetch } = useQuery(GET_SETTLEMENT_OVERVIEW, {
        variables: {
            catalogId: user?.selectedCatalog?.id,
            status: filters.status || null,
            startDate: filters.startDate || null,
            endDate: filters.endDate || null,
            page,
            limit: ITEMS_PER_PAGE
        },
        skip: !user?.selectedCatalog?.id,
        fetchPolicy: 'network-only' // Add this to ensure fresh data
    });

    const [settleNow, { loading: settleLoading }] = useMutation(SETTLE_NOW, {
        onCompleted: (data) => {
            toast.success(data.settleNow.message);
            // Refetch settlements after successful settlement
            refetch();
        },
        onError: (error) => {
            toast.error(error.message);
        }
    });

    const [verifyingReference, setVerifyingReference] = useState(null);
    const [verifySettlement] = useMutation(VERIFY_SETTLEMENT, {
        onCompleted: (data) => {
            if (data.verifySettlement.success) {
                toast.success('Settlement verified successfully');
                refetch(); // Refresh the settlements list
            } else {
                toast.error(data.verifySettlement.message);
            }
            setVerifyingReference(null);
        },
        onError: (error) => {
            toast.error(error.message);
            setVerifyingReference(null);
        }
    });

    const handleSettleNow = async () => {
        try {
            await settleNow({
                variables: {
                    catalogId: user?.selectedCatalog?.id
                }
            });
        } catch (error) {
            console.error('Settlement error:', error);
        }
    };

    const handleVerifySettlement = async (reference) => {
        setVerifyingReference(reference);
        try {
            await verifySettlement({
                variables: { reference }
            });
        } catch (error) {
            console.error('Settlement verification error:', error);
        }
    };

    if (loading) return <LoadingSpinner />;
    if (error) {
        return (
            <Layout>
                <div className="flex items-center gap-2 p-4 text-red-700 bg-red-100 rounded-md">
                    <AlertCircle className="w-5 h-5" />
                    <span>{error.message}</span>
                </div>
            </Layout>
        );
    }

    const settlements = data?.getSettlementOverview?.settlements?.edges || [];
    const totalSettledAmount = data?.getSettlementOverview?.totalSettledAmount || 0;
    const pendingSettlementAmount = data?.getSettlementOverview?.pendingSettlementAmount || 0;

    return (
        <Layout>
            <div className="p-4 pb-12">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-bold">Settlements</h1>
                    <button
                        onClick={handleSettleNow}
                        disabled={settleLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {settleLoading ? (
                            <>
                                <span className="animate-spin">⚬</span>
                                Processing...
                            </>
                        ) : (
                            <>
                                <span>Settle Now</span>
                            </>
                        )}
                    </button>
                </div>

                {/* Filters */}
                <div className="mb-4 flex flex-wrap gap-4">
                    <select
                        className="form-select rounded-md"
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                        <option value="">All Statuses</option>
                        <option value="PENDING">Pending</option>
                        <option value="PROCESSING">Processing</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="FAILED">Failed</option>
                    </select>

                    <input
                        type="date"
                        className="form-input rounded-md"
                        value={filters.startDate}
                        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    />

                    <input
                        type="date"
                        className="form-input rounded-md"
                        value={filters.endDate}
                        onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    />
                </div>

                {/* Settlement Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg shadow">
                        <h3 className="text-lg font-semibold mb-2">Total Settled</h3>
                        <p className="text-2xl text-green-600">₦{totalSettledAmount.toLocaleString()}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow">
                        <h3 className="text-lg font-semibold mb-2">Pending Settlement</h3>
                        <p className="text-2xl text-yellow-600">₦{pendingSettlementAmount.toLocaleString()}</p>
                    </div>
                </div>

                {/* Settlements Table */}
                <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Settlement History</h2>
                    {settlements.length === 0 ? (
                        <div className="text-center py-8 bg-white rounded-lg">
                            <p className="text-gray-500">No settlements found</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Initiated</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Settled</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {settlements.map(({ node }) => (
                                        <tr key={node.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">{node.reference}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">₦{node.amount.toLocaleString()}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${SettlementStatus[node.status]}`}>
                                                    {node.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDateTime(node.dateInitiated)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">{node.dateSettled ? formatDateTime(node.dateSettled) : '-'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {node.status === 'PROCESSING' && (
                                                    <button
                                                        onClick={() => handleVerifySettlement(node.reference)}
                                                        disabled={verifyingReference === node.reference}
                                                        className="inline-flex items-center px-3 py-1 text-sm text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 disabled:opacity-50"
                                                    >
                                                        {verifyingReference === node.reference ? (
                                                            <>
                                                                <span className="animate-spin mr-2">⚬</span>
                                                                Verifying...
                                                            </>
                                                        ) : (
                                                            'Verify Settlement'
                                                        )}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Completed Transactions Table */}
                <div className="mt-8">
                    <h2 className="text-xl font-semibold mb-4">Completed Transactions</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Settlement Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data?.getSettlementOverview.completedTransactions.edges.map(({ node }) => (
                                    <tr key={node.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{node.reference}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {node.customer.name || node.customer.phone || 'Anonymous'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">₦{node.amount.toLocaleString()}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${SettlementStatus[node.settlementStatus]}`}>
                                                {node.settlementStatus}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDateTime(node.dateCreated)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                {/* ...existing pagination code... */}
            </div>
        </Layout>
    );
};

export default Settlements;
